import React from 'react';
import { AppLayout } from '../components/AppLayout';
import LandingHeader from '../components/LandingHeader';
import Footer from '../../components/Footer';
import { isIOSDevice } from '../utils/deviceDetection';
import { useAuth } from '../components/AuthProviderWrapper';
import { BackButton } from '../components/BackButton';

const PrivacyPolicy: React.FC = () => {
  const isIOS = isIOSDevice();
  const { isAuthenticated } = useAuth();
  
  const content = (
    <div className="max-w-4xl mx-auto px-4" style={{ 
      paddingTop: isAuthenticated && isIOS ? '5rem' : '2rem', 
      paddingBottom: '2rem' 
    }}>
      <div className="mb-4">
        <BackButton />
      </div>
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Privacy Policy</h1>
      
      <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        <div>
          <p className="text-gray-600 mb-4">
            <strong>Last Updated:</strong> January 2025
          </p>
          <p className="text-gray-600">
            DinnerBrain (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) is committed to protecting your privacy. This Privacy Policy 
            explains how we collect, use, disclose, and safeguard your information when you use our recipe 
            management application.
          </p>
        </div>

        <section>
          <h2 className="text-xl font-semibold mb-3">Information We Collect</h2>
          <div className="space-y-3 text-gray-600">
            <div>
              <h3 className="font-semibold text-gray-700">Personal Information</h3>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Email address</li>
                <li>Name (optional)</li>
                <li>Profile information you choose to provide</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-700">Recipe Data</h3>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Recipes you create, import, or save</li>
                <li>Meal plans and shopping lists</li>
                <li>Recipe ratings and favorites</li>
                <li>Notes and modifications to recipes</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-700">Usage Information</h3>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>App features you use</li>
                <li>Time spent in the app</li>
                <li>Device information and browser type</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">How We Use Your Information</h2>
          <ul className="list-disc pl-5 text-gray-600 space-y-2">
            <li>To provide and maintain our service</li>
            <li>To personalize your experience</li>
            <li>To sync your recipes across devices</li>
            <li>To send you updates about your account</li>
            <li>To improve our app based on usage patterns</li>
            <li>To respond to your support requests</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Data Storage and Security</h2>
          <p className="text-gray-600">
            We use industry-standard security measures to protect your data:
          </p>
          <ul className="list-disc pl-5 text-gray-600 space-y-2 mt-3">
            <li>All data is encrypted in transit using HTTPS</li>
            <li>Passwords are hashed using secure algorithms</li>
            <li>Regular security audits and updates</li>
            <li>Secure cloud storage with automatic backups</li>
            <li>Access controls and authentication measures</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Data Sharing</h2>
          <p className="text-gray-600 mb-3">
            We do not sell, trade, or rent your personal information to third parties. We may share your 
            information only in the following situations:
          </p>
          <ul className="list-disc pl-5 text-gray-600 space-y-2">
            <li>With your explicit consent</li>
            <li>To comply with legal obligations</li>
            <li>To protect our rights and prevent fraud</li>
            <li>With service providers who help us operate the app (under strict confidentiality agreements)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Alexa Voice Integration</h2>
          <p className="text-gray-600 mb-3">
            DinnerBrain offers an optional Alexa skill that allows you to add items to your shopping list using voice commands through Alexa-enabled devices.
          </p>
          
          <div className="space-y-4 text-gray-600">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">How It Works:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>When you enable the DinnerBrain Alexa skill and speak commands like &quot;Alexa, tell Dinner Brain to add milk,&quot; your voice is processed by Amazon&apos;s servers</li>
                <li>Amazon converts your voice to text and sends only the text interpretation to DinnerBrain</li>
                <li>We receive only the shopping list items you want to add - no voice recordings</li>
                <li>DinnerBrain does not have access to your actual voice recordings</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Account Linking:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>You must link your DinnerBrain account with Alexa to use this feature</li>
                <li>Account linking uses OAuth 2.0 for secure authentication</li>
                <li>You can unlink accounts at any time through the Alexa app Settings</li>
                <li>Unlinking immediately stops all data sharing between the services</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Data We Receive from Alexa:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Text of items you want to add to your shopping list</li>
                <li>Basic usage analytics (frequency of skill use)</li>
                <li>No personal information from your Amazon account</li>
                <li>No purchase history or payment information</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Data We Don&apos;t Share with Amazon:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your existing DinnerBrain shopping lists</li>
                <li>Your meal plans or recipes</li>
                <li>Your account profile information</li>
                <li>Any data beyond confirming successful item additions</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Your Control:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>The Alexa skill is completely optional</li>
                <li>Disable the skill anytime via the Alexa app</li>
                <li>Items added through Alexa can be edited or deleted in DinnerBrain</li>
                <li>Review and delete your Alexa voice history at any time in the Alexa app</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Third-Party Privacy:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Voice processing is governed by Amazon&apos;s Privacy Policy</li>
                <li>Review Amazon&apos;s Alexa privacy practices at <a href="https://www.amazon.com/alexaprivacy" className="text-[#6E9C87] hover:underline">www.amazon.com/alexaprivacy</a></li>
                <li>DinnerBrain is not responsible for Amazon&apos;s data handling practices</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Changes to This Policy:</h3>
              <p>Your continued use of the DinnerBrain Alexa skill after any privacy policy updates constitutes acceptance of those changes.</p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Your Rights and Choices</h2>
          <p className="text-gray-600 mb-3">You have the right to:</p>
          <ul className="list-disc pl-5 text-gray-600 space-y-2">
            <li>Access your personal information</li>
            <li>Correct or update your information</li>
            <li>Delete your account and associated data</li>
            <li>Export your recipes and data</li>
            <li>Opt-out of non-essential communications</li>
            <li>Request a copy of your data</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Cookies and Tracking</h2>
          <p className="text-gray-600">
            We use essential cookies to maintain your session and preferences. We do not use tracking 
            cookies for advertising purposes. You can control cookie settings in your browser.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Children&apos;s Privacy</h2>
          <p className="text-gray-600">
            Our service is not intended for children under 13 years of age. We do not knowingly collect 
            personal information from children under 13.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Changes to This Policy</h2>
          <p className="text-gray-600">
            We may update this Privacy Policy from time to time. We will notify you of any changes by 
            posting the new Privacy Policy on this page and updating the &ldquo;Last Updated&rdquo; date.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">Contact Us</h2>
          <p className="text-gray-600">
            If you have questions about this Privacy Policy or how we handle your data, please contact us at:
          </p>
          <p className="text-gray-600 mt-3">
            Email: <a href="mailto:contact-us@dinner-brain.com" className="text-[#6E9C87] hover:underline">contact-us@dinner-brain.com</a>
          </p>
        </section>
      </div>
    </div>
  );

  // If logged in, use AppLayout with regular header
  if (isAuthenticated) {
    return (
      <AppLayout>
        {content}
      </AppLayout>
    );
  }

  // If logged out, use LandingHeader
  return (
    <div className="min-h-screen bg-white">
      <LandingHeader />
      <div style={{ 
        paddingTop: isIOS ? 'calc(5rem + env(safe-area-inset-top, 0px))' : '5rem'
      }}>
        {content}
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;